import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { scopedStorage } from '@lark-apaas/client-toolkit-lite';
import userStorage, { getCurrentUserId } from '@/lib/user-storage';
import { resolveMemberTrainingDay, type MemberTrainingSource } from '@/lib/memberTrainingDay';

/**
 * 进行中训练的全局状态（Context 单例）。
 *
 * 为什么用 Context 而不是各自 useState：
 * - Layout / WorkoutPage / TrainingPage / DayDetailPage 都需要读写 activeWorkout
 * - 若各自 useState + scopedStorage 同步，endWorkout 只改了 WorkoutPage 的副本，
 *   Layout 的 state 仍是 true → Tab 锁定无法解除（历史实测 bug）
 * - Context 保证全应用单一真相源，endWorkout 一次调用所有订阅点同步更新
 *
 * 生命周期设计：
 *   startWorkout()   — DayDetailPage「开始训练」按钮 / TrainingPage「跟练」
 *                      / WorkoutPage 首次挂载兜底（无状态时自动恢复）
 *   进行中           — active=true，Tab 锁定（点其他 tab 弹 toast 并回训练页）
 *   finishWorkout()  — 训练完成（打卡 + 清除 + 跳 DayDetail / TrainingPage）
 *   quitWorkout()    — 手动终止（保存已完成组 + 清除 + 跳 TrainingPage）
 *   endWorkout()     — 底层清除（finish/quit 都调用它）
 *
 * 与焚决隔离：FenJueWorkoutPage 有自己独立的 fen_jue_plan_* storage，
 * 不使用本 Context，因此互不干扰。
 */

export interface IActiveWorkout {
  active: boolean;
  dayId: string;
  dayName: string;
  courseId?: string;
  courseName?: string;
  startTimestamp: number;
  executionSnapshot?: MemberTrainingSource;
}

const ACTIVE_WORKOUT_KEY = 'active_workout';

const DEFAULT_ACTIVE_WORKOUT: IActiveWorkout = {
  active: false,
  dayId: '',
  dayName: '',
  startTimestamp: 0,
};

// 训练会话有效期（毫秒）：超过 2 小时视为已过期，自动清除，避免脏数据锁死 Tab
export const WORKOUT_SESSION_TTL_MS = 2 * 60 * 60 * 1000;

export interface WorkoutValidationResult {
  valid: boolean;
  reason?: 'empty' | 'parse_error' | 'missing_fields' | 'invalid_active' | 'expired';
  data: IActiveWorkout | null;
}

/**
 * 校验训练数据合法性（可被 Layout / 其他工具直接调用做自救判断）。
 * 返回 valid=true 当且仅当：字段完整 + active=true 时有合法 dayId + 未过期。
 */
export function validateActiveWorkout(raw: string | null | undefined): WorkoutValidationResult {
  if (!raw) return { valid: false, reason: 'empty', data: null };
  try {
    const parsed = JSON.parse(raw) as Partial<IActiveWorkout>;
    // 关键字段完整性校验
    if (
      typeof parsed.active !== 'boolean' ||
      typeof parsed.dayId !== 'string' ||
      typeof parsed.startTimestamp !== 'number' ||
      typeof parsed.dayName !== 'string'
    ) {
      return { valid: false, reason: 'missing_fields', data: null };
    }
    // active=true 时必须有 dayId 和合理的 startTimestamp
    if (parsed.active && (!parsed.dayId || parsed.startTimestamp <= 0)) {
      return { valid: false, reason: 'invalid_active', data: null };
    }
    // 过期保护：超过 2 小时的训练记录自动作废
    if (parsed.active && Date.now() - parsed.startTimestamp > WORKOUT_SESSION_TTL_MS) {
      return { valid: false, reason: 'expired', data: null };
    }
    return { valid: parsed.active, data: parsed as IActiveWorkout };
  } catch {
    return { valid: false, reason: 'parse_error', data: null };
  }
}

function loadActiveWorkout(): IActiveWorkout {
  try {
    const raw = userStorage.getItem(ACTIVE_WORKOUT_KEY);
    const result = validateActiveWorkout(raw);
    return result.data ?? DEFAULT_ACTIVE_WORKOUT;
  } catch {
    return DEFAULT_ACTIVE_WORKOUT;
  }
}

interface ActiveWorkoutContextValue {
  activeWorkout: IActiveWorkout;
  /** 开始训练（写入内存 + 持久化到 scopedStorage） */
  startWorkout: (info: Omit<IActiveWorkout, 'active' | 'startTimestamp'>) => void;
  /** 清除训练状态（内存 + scopedStorage 同步清空） */
  endWorkout: () => void;
}

const ActiveWorkoutContext = createContext<ActiveWorkoutContextValue | null>(null);

/** 顶层 Provider：在 App 根部挂载一次，保证全应用单例 */
export function ActiveWorkoutProvider({ children }: { children: ReactNode }) {
  const owner = getCurrentUserId();
  // 🔴 首帧同步初始化：立即从 scopedStorage 读一次并校验合法性，
  // 避免首帧 active=false → 第二帧 active=true 的闪烁导致：
  //   ① TabBar 先显示后隐藏（视觉闪烁）
  //   ② 用户在首帧窗口点 Tab → 第二帧被拦截 → 体验上像「点了没反应」
  // 同步读取是安全的：localStorage.getItem 是微秒级同步操作，不阻塞首帧。
  const [activeWorkout, setActiveWorkout] = useState<IActiveWorkout>(() => {
    try {
      const raw = userStorage.getItem(ACTIVE_WORKOUT_KEY);
      const result = validateActiveWorkout(raw);
      // 如果读到脏数据，同步就清掉（不等 useEffect），保证首帧就是干净状态
      if (raw && !result.valid) {
        userStorage.removeItem(ACTIVE_WORKOUT_KEY);
      }
      return result.data ?? DEFAULT_ACTIVE_WORKOUT;
    } catch {
      return DEFAULT_ACTIVE_WORKOUT;
    }
  });

  // useEffect 作为二次兜底：兼容 StrictMode 双调用 / 其他 tab 修改 storage 等边缘场景
  useEffect(() => {
    const raw = userStorage.getItem(ACTIVE_WORKOUT_KEY);
    const result = validateActiveWorkout(raw);
    const loaded = result.data ?? DEFAULT_ACTIVE_WORKOUT;
    setActiveWorkout(prev => {
      // 只有真的不同才 set，避免无谓重渲染
      if (prev.active === loaded.active && prev.dayId === loaded.dayId && prev.startTimestamp === loaded.startTimestamp) {
        return prev;
      }
      return loaded;
    });
    // 主动清脏：storage 有值但非法 → 清掉
    if (raw && !result.valid) {
      userStorage.removeItem(ACTIVE_WORKOUT_KEY);
    }
  }, []);

  // 持久化：active=true 时写入，false 时清除 key
  useEffect(() => {
    if (getCurrentUserId() !== owner) return;
    if (activeWorkout.active) {
      userStorage.setItem(ACTIVE_WORKOUT_KEY, JSON.stringify(activeWorkout));
    } else {
      userStorage.removeItem(ACTIVE_WORKOUT_KEY);
    }
  }, [activeWorkout, owner]);

  const startWorkout = useCallback(
    (info: Omit<IActiveWorkout, 'active' | 'startTimestamp'>) => {
      if (getCurrentUserId() !== owner) return;
      setActiveWorkout({
        ...info,
        executionSnapshot: info.dayId.startsWith('member-') ? resolveMemberTrainingDay(info.dayId) || undefined : undefined,
        active: true,
        startTimestamp: Date.now(),
      });
    },
    [owner],
  );

  // 🔴 同步版 endWorkout：立即写 state + 立即清 storage，不等 useEffect 调度。
  // 为什么需要同步清：退出训练时 Layout/TabBar 会在下一次 render 前读到 activeWorkout，
  // 如果只 setState 不同步清 storage，navigate 触发的重渲染可能仍带旧值（React 批处理），
  // 导致 Tab 软拦截仍生效 → 把用户拉回 /workout → 兜底 useEffect 又 start → 死循环。
  const endWorkout = useCallback(() => {
    if (getCurrentUserId() !== owner) return;
    const cleared: IActiveWorkout = {
      active: false,
      dayId: '',
      dayName: '',
      startTimestamp: 0,
    };
    setActiveWorkout(cleared);
    // 同步清 storage，确保 Layout 下次任何时机读取都是干净的
    userStorage.removeItem(ACTIVE_WORKOUT_KEY);
  }, [owner]);

  return (
    <ActiveWorkoutContext.Provider value={{ activeWorkout, startWorkout, endWorkout }}>
      {children}
    </ActiveWorkoutContext.Provider>
  );
}

/**
 * 消费 Hook：全应用共用同一份状态
 * @throws 如果在 Provider 外调用会抛错（避免开发期误用）
 */
export function useActiveWorkout(): ActiveWorkoutContextValue {
  const ctx = useContext(ActiveWorkoutContext);
  if (!ctx) {
    throw new Error('useActiveWorkout must be used within <ActiveWorkoutProvider>');
  }
  return ctx;
}
